import { FC, ReactNode, useEffect, useState } from 'react';
import { Tabs } from 'antd';
import { TbActivity, TbMessages } from 'react-icons/tb';

import { ResourceActivityResourceType } from '@/types';

import ResourceActivityFeed from '../ResourceActivityFeed';
import { subscribeResourceActivityOpenEvents } from '../resourceCommentEvents';
import ResourceCommentsPanel from '../ResourceCommentsPanel';

import './index.css';

export type ResourceActivitySiderTab = 'activity' | 'comments';

interface ResourceActivitySiderProps {
    activeTab?: ResourceActivitySiderTab;
    authorizationResourceId?: string;
    canComment?: boolean;
    canExportActivity?: boolean;
    focusedCommentId?: string;
    height?: number | string;
    onTabChange?: (tab: ResourceActivitySiderTab) => void;
    resourceId: string;
    resourceTitle?: ReactNode;
    resourceType: ResourceActivityResourceType;
    resourceWebsite?: string;
}

const ResourceActivitySider: FC<ResourceActivitySiderProps> = ({
    activeTab,
    authorizationResourceId,
    canComment,
    canExportActivity,
    focusedCommentId,
    height = '100%',
    onTabChange,
    resourceId,
    resourceTitle,
    resourceType,
    resourceWebsite,
}): JSX.Element => {
    const [internalActiveTab, setInternalActiveTab] = useState<ResourceActivitySiderTab>('comments');
    const selectedTab = activeTab ?? internalActiveTab;

    useEffect(
        () =>
            subscribeResourceActivityOpenEvents((detail) => {
                if (detail.resourceId !== resourceId || detail.resourceType !== resourceType || !detail.tab) {
                    return;
                }

                setInternalActiveTab(detail.tab);
                onTabChange?.(detail.tab);
            }),
        [onTabChange, resourceId, resourceType],
    );

    return (
        <Tabs
            className='resource-activity-sider-tabs'
            destroyOnHidden
            style={{ height, minHeight: 0 }}
            activeKey={selectedTab}
            onChange={(key) => {
                const tab = key as ResourceActivitySiderTab;

                setInternalActiveTab(tab);
                onTabChange?.(tab);
            }}
            tabBarStyle={{ padding: '0 0.5rem', marginBottom: 0 }}
            size='small'
            items={[
                {
                    key: 'comments',
                    label: (
                        <>
                            <TbMessages style={{ verticalAlign: '-0.125rem', marginRight: '0.375rem' }} />
                            Comments
                        </>
                    ),
                    children: (
                        <div
                            style={{
                                boxSizing: 'border-box',
                                height: '100%',
                                minHeight: 0,
                                overflow: 'hidden',
                                padding: '0.75rem',
                            }}
                        >
                            <ResourceCommentsPanel
                                canComment={canComment}
                                focusedCommentId={focusedCommentId}
                                resourceType={resourceType}
                                resourceId={resourceId}
                                resourceTitle={typeof resourceTitle === 'string' ? resourceTitle : undefined}
                                height='100%'
                                resourceWebsite={resourceWebsite}
                            />
                        </div>
                    ),
                },
                {
                    key: 'activity',
                    label: (
                        <>
                            <TbActivity style={{ verticalAlign: '-0.125rem', marginRight: '0.375rem' }} />
                            Activity
                        </>
                    ),
                    children: (
                        <div
                            style={{
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                minHeight: 0,
                                overflow: 'hidden',
                                padding: '0.75rem',
                            }}
                        >
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <ResourceActivityFeed
                                    authorizationResourceId={authorizationResourceId}
                                    canExport={canExportActivity}
                                    resourceType={resourceType}
                                    resourceId={resourceId}
                                    resourceTitle={resourceTitle}
                                    height='100%'
                                    resourceWebsite={resourceWebsite}
                                />
                            </div>
                        </div>
                    ),
                },
            ]}
        />
    );
};

export default ResourceActivitySider;
